import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Box, Button, Fade, Snackbar, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { WorkflowProvider } from '../../contexts/WorkflowContext';
import { useWorkflow } from '../../hooks/useWorkflow';
import { useAuth } from '../../hooks/useAuth';
import { workflowService } from '../../services/workflowService';
import { stagingService } from '../../services/stagingService';
import { BatchWorkflowListItem, BulkStatusUpdateResponse } from '../../types/workflow';
import {
  WorkflowHeader,
  WorkflowToolbar,
  WorkflowEmptyState,
  WorkflowErrorState,
  WorkflowBulkActions,
  BulkStatusUpdateDialog,
} from './components';
import { WorkflowListView } from './views/WorkflowListView';

/**
 * WorkflowDashboard Container Component
 *
 * A thin orchestration component following SOLID principles:
 * - Single Responsibility: ONLY composes layout and handles local UI state (dialogs)
 * - No data fetching, no filter state management
 * - All business logic delegated to useWorkflow hook via WorkflowContext
 *
 * Structure:
 * - Outer component (WorkflowDashboard) provides WorkflowContext
 * - Inner component (WorkflowDashboardContent) consumes context
 */
export const WorkflowDashboard: React.FC = () => {
  return (
    <WorkflowProvider>
      <WorkflowDashboardContent />
    </WorkflowProvider>
  );
};

/** Inner content component that consumes the workflow context. */
const WorkflowDashboardContent: React.FC = () => {
  const workflow = useWorkflow();
  const { clearSelection, refetch } = workflow;
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  // Bulk status update state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [reviewingBatchId, setReviewingBatchId] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleRowClick = useCallback((batch: BatchWorkflowListItem) => {
    if (!isAdmin) return;
    navigate(`/workflow/${batch.id}`);
  }, [navigate, isAdmin]);

  const handleOpenWorkspace = useCallback((batch: BatchWorkflowListItem) => {
    const targetBatchId = batch.batchId ?? batch.soilBatchId;
    if (targetBatchId) {
      navigate(`/workspace/${targetBatchId}`, { state: { from: `/workflow/${batch.id}` } });
    }
  }, [navigate]);

  const handleReviewSamples = useCallback(async (batch: BatchWorkflowListItem) => {
    setReviewingBatchId(batch.id);
    try {
      const result = await workflowService.validateSampleFiles(batch.id);
      if (!result.isValid) {
        setSnackbar({ open: true, message: `Validation failed: ${result.totalErrors} error(s) found. Open the workflow detail to review.`, severity: 'error' });
        return;
      }
      const response = await stagingService.createFromWorkflow(batch.id);
      if (response.success && response.data?.sessionId) {
        navigate(`/staging/review/${response.data.sessionId}`);
      } else {
        setSnackbar({ open: true, message: response.message || 'Failed to create staging session', severity: 'error' });
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } };
      const msg = axiosErr?.response?.status === 403
        ? 'You do not have permission to review samples. Please contact an administrator.'
        : err instanceof Error ? err.message : 'Failed to review samples';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setReviewingBatchId(null);
    }
  }, [navigate]);

  const handleBulkStatusChange = useCallback(() => {
    setBulkDialogOpen(true);
  }, []);

  // Export the CURRENT filtered view — same filters the list uses, but the server
  // ignores pagination so every matching row lands in the spreadsheet.
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      await workflowService.exportBatches({
        statusCode: workflow.filters.statusCode || undefined,
        priority: workflow.filters.priority || undefined,
        isOverdue: workflow.filters.overdueOnly || undefined,
        includeCompleted: workflow.filters.showCompleted || undefined,
        searchTerm: workflow.filters.search || undefined,
        sortBy: workflow.filters.sortBy,
        sortOrder: workflow.filters.sortOrder,
      });
    } catch {
      setSnackbar({ open: true, message: 'Failed to export the workflow list', severity: 'error' });
    } finally {
      setExporting(false);
    }
  }, [workflow.filters]);

  const handleBulkUpdateComplete = useCallback((result: BulkStatusUpdateResponse) => {
    clearSelection();
    refetch();

    if (result.failureCount === 0) {
      setSnackbar({
        open: true,
        message: `Successfully updated ${result.successCount} batch${result.successCount !== 1 ? 'es' : ''}`,
        severity: 'success',
      });
    } else if (result.successCount === 0) {
      setSnackbar({
        open: true,
        message: `Failed to update all ${result.failureCount} batch${result.failureCount !== 1 ? 'es' : ''}`,
        severity: 'error',
      });
    } else {
      setSnackbar({
        open: true,
        message: `Updated ${result.successCount} batch${result.successCount !== 1 ? 'es' : ''}, ${result.failureCount} failed`,
        severity: 'info',
      });
    }
  }, [clearSelection, refetch]);

  return (
    <Fade in timeout={500}>
      <Box>
        {/* Page Header with Create Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <WorkflowHeader
            title="Batch Workflow"
            subtitle="Track and manage batch workflows through lab processing and delivery"
          />
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/workflow/new')}
              sx={{ flexShrink: 0 }}
            >
              Create Workflow
            </Button>
          )}
        </Box>

        {/* Toolbar: Search, Filters, Refresh */}
        <WorkflowToolbar
          searchValue={workflow.filters.search}
          onSearchChange={(value) => workflow.setFilter('search', value)}
          onRefresh={workflow.refetch}
          loading={workflow.loading}
          filters={workflow.filters}
          onFilterChange={workflow.setFilter}
          onReset={workflow.resetFilters}
          activeFilterCount={workflow.activeFilterCount}
          onExport={handleExport}
          exporting={exporting}
        />

        {/* Bulk Actions (conditional, admin only) */}
        {isAdmin && workflow.selectedCount > 0 && (
          <WorkflowBulkActions
            selectedCount={workflow.selectedCount}
            onClearSelection={workflow.clearSelection}
            onBulkStatusChange={handleBulkStatusChange}
            disabled={false}
          />
        )}

        {/* Main Content: Error State, Empty State, or List View */}
        {workflow.error ? (
          <WorkflowErrorState
            error={workflow.error}
            onRetry={workflow.refetch}
          />
        ) : workflow.batches.length === 0 && !workflow.loading ? (
          <WorkflowEmptyState
            hasFilters={workflow.hasActiveFilters}
            onClearFilters={workflow.resetFilters}
          />
        ) : (
          <WorkflowListView
            batches={workflow.batches}
            loading={workflow.batchesLoading}
            page={workflow.filters.page}
            pageSize={workflow.filters.pageSize}
            totalCount={workflow.totalCount}
            onPageChange={(page) => workflow.setFilter('page', page)}
            onPageSizeChange={(size) => workflow.setFilter('pageSize', size)}
            sortBy={workflow.filters.sortBy}
            sortOrder={workflow.filters.sortOrder}
            onSortChange={(sortBy, sortOrder) => workflow.setFilters({ sortBy, sortOrder })}
            selectedIds={workflow.selectedIds}
            onToggleSelect={workflow.toggleSelection}
            onSelectAll={workflow.selectAll}
            onRowClick={handleRowClick}
            showActions={!isAdmin}
            showCheckbox={isAdmin}
            onOpenWorkspace={handleOpenWorkspace}
            onReviewSamples={handleReviewSamples}
            reviewingBatchId={reviewingBatchId}
          />
        )}

        {/* Bulk Status Update Dialog */}
        <BulkStatusUpdateDialog
          open={bulkDialogOpen}
          onClose={() => setBulkDialogOpen(false)}
          selectedBatchIds={[...workflow.selectedIds]}
          onUpdateComplete={handleBulkUpdateComplete}
        />

        {/* Snackbar for feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
};

export default WorkflowDashboard;
